import { z } from 'zod';

/**
 * POST /api/provision-jobs/runtimeenv リクエスト・レスポンスの型定義
 * RuntimeEnv払い出し機能③のAPI仕様
 */

const FIXED_TARGET_REVISION_BY_ENV = {
  dev: 'develop',
  stg: 'staging',
  prod: 'main',
} as const;

/** appcis配列の1要素（Repository/Branch/Manifest Pathの組）の Zod スキーマ */
const ProvisionJobRuntimeEnvAppCISchema = z.object({
  repo: z.string().min(1).max(255),
  manifest_path: z.string().min(1).max(100),
  target_revision: z.string().min(1).max(100),
});

/** POST /api/provision-jobs/runtimeenv リクエストボディの Zod スキーマ */
export const ProvisionJobRuntimeEnvRequestSchema = z
  .object({
    product_id: z.string().uuid('product_id must be a valid UUID'),
    workspace_id: z.string().uuid('workspace_id must be a valid UUID'),
    env: z.enum(['dev', 'stg', 'prod', 'feature']),
    env_name: z.string().min(1).max(255),
    appcis: z.array(ProvisionJobRuntimeEnvAppCISchema).min(1).max(20),
  })
  .superRefine((data, ctx) => {
    const fixedRevision =
      FIXED_TARGET_REVISION_BY_ENV[
        data.env as keyof typeof FIXED_TARGET_REVISION_BY_ENV
      ];
    const seenRepos = new Set<string>();
    data.appcis.forEach((appci, index) => {
      if (fixedRevision && appci.target_revision !== fixedRevision) {
        ctx.addIssue({
          code: 'custom',
          path: ['appcis', index, 'target_revision'],
          message: `target_revision must be ${fixedRevision} for ${data.env}`,
        });
      }
      if (seenRepos.has(appci.repo)) {
        ctx.addIssue({
          code: 'custom',
          path: ['appcis', index, 'repo'],
          message: 'repo must not be duplicated within appcis',
        });
      }
      seenRepos.add(appci.repo);
    });
  });

/** POST /api/provision-jobs/runtimeenv リクエストボディの型 */
export type ProvisionJobRuntimeEnvRequest = z.infer<
  typeof ProvisionJobRuntimeEnvRequestSchema
>;

/** POST /api/provision-jobs/runtimeenv レスポンスボディの Zod スキーマ（202 Accepted） */
export const ProvisionJobRuntimeEnvResponseSchema = z.object({
  job_id: z.string(),
  request_id: z.string(),
  status: z.enum(['in_progress', 'completed', 'failed']),
  message: z.string(),
});

/** POST /api/provision-jobs/runtimeenv レスポンスボディの型 */
export type ProvisionJobRuntimeEnvResponse = z.infer<
  typeof ProvisionJobRuntimeEnvResponseSchema
>;
